import React, { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, addDoc, serverTimestamp } from 'firebase/firestore';
import { Calendar, MapPin, Check, Loader2, AlertCircle, ChevronDown, ArrowLeft } from 'lucide-react';
import { db } from '../config/firebase';
import useAppStore from '../store/appStore';
import DsgvoModal from '../components/DsgvoModal';
import { isSeminarDateExpired, parseSeminarEndDate } from '../utils/seminarArchive';
import {
  SeminarScheduleDay,
  normalizeScheduleDays,
  sortScheduleDays,
  formatScheduleDayLine,
  resolveSeminarDateText,
} from '../utils/seminarSchedule';
import {
  SeminarPromoCode,
  normalizePromoCodes,
  findMatchingPromoCode,
  calculateSeminarPricingWithPromo,
} from '../utils/seminarPromoCodes';

interface PublicSeminar {
  id: string;
  title: string;
  date: string;
  scheduleDays?: SeminarScheduleDay[];
  location: string;
  locationAddress?: string;
  marketingTeaser?: string;
  marketingBullets?: string[];
  marketingPitch?: string;
  marketingImage?: string;
  priceBestand: number;
  priceExtern: number;
  priceZusatz: number;
  promoCodes?: SeminarPromoCode[];
  isOnline?: boolean;
}

interface RegistrationFormData {
  vorname: string;
  nachname: string;
  firma: string;
  email: string;
  telefon: string;
  strasse: string;
  plz: string;
  ort: string;
  agbAccepted: boolean;
  photoConsent: boolean;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^[\d\s+-]{6,}$/;

const formatEuro = (amount: number) =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);

const inputClasses =
  'bg-pe-input dark:bg-[#0a0a0a] border border-pe-border-strong dark:border-[#333] text-pe-text dark:text-white rounded-xl p-4 w-full font-sans text-sm focus:outline-none focus:border-[#8D6435] dark:focus:border-[#E6C299] focus:ring-1 focus:ring-[#8D6435]/30 dark:focus:ring-[#E6C299]/30 transition-all';

const EMPTY_FORM: RegistrationFormData = {
  vorname: '',
  nachname: '',
  firma: '',
  email: '',
  telefon: '',
  strasse: '',
  plz: '',
  ort: '',
  agbAccepted: false,
  photoConsent: false,
};

const SeminarePage: React.FC = () => {
  const clientData = useAppStore((s: any) => s.clientData);

  const [seminars, setSeminars] = useState<PublicSeminar[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const [form, setForm] = useState<RegistrationFormData>(EMPTY_FORM);
  const [mandantenNummer, setMandantenNummer] = useState('');
  const [zusatzPersonen, setZusatzPersonen] = useState(0);
  const [promoCodeInput, setPromoCodeInput] = useState('');
  const [promoCodeError, setPromoCodeError] = useState<string | null>(null);
  const [isPitchExpanded, setIsPitchExpanded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [invalidFields, setInvalidFields] = useState<string[]>([]);
  const [isDsgvoModalOpen, setIsDsgvoModalOpen] = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'public_events'),
      (snap) => {
        const list: PublicSeminar[] = snap.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            title: data.title || 'Seminar',
            date: data.date || '',
            scheduleDays: normalizeScheduleDays(data.scheduleDays),
            location: data.location || '',
            locationAddress: data.locationAddress || '',
            marketingTeaser: data.marketingTeaser || '',
            marketingBullets: Array.isArray(data.marketingBullets) ? data.marketingBullets : [],
            marketingPitch: data.marketingPitch || '',
            marketingImage: data.marketingImage || '',
            priceBestand: Number(data.priceBestand) || 0,
            priceExtern: Number(data.priceExtern) || 0,
            priceZusatz: Number(data.priceZusatz) || 0,
            promoCodes: normalizePromoCodes(data.promoCodes),
            isOnline: data.isOnline === true,
          };
        });
        setSeminars(list);
        setIsLoading(false);
      },
      (err) => {
        console.error('[SeminarePage] public_events:', err);
        setIsLoading(false);
      }
    );
    return () => unsub();
  }, []);

  // Prefill aus Mandanten-Stammdaten
  useEffect(() => {
    if (!clientData) return;
    const cd = clientData.clientData ? { ...clientData, ...clientData.clientData } : clientData;
    setMandantenNummer(String(cd.mandantNr || cd.providedOldId || '').trim());
    setForm((prev) => ({
      ...prev,
      vorname: prev.vorname || String(cd.vorname || cd.unternehmerVorname || '').trim(),
      nachname: prev.nachname || String(cd.nachname || cd.unternehmerNachname || '').trim(),
      firma: prev.firma || String(cd.firmenname || '').trim(),
      email: prev.email || String(cd.email || cd.mailadresse || '').trim(),
      telefon: prev.telefon || String(cd.telefon || cd.handy || cd.whatsapp || '').trim(),
      strasse: prev.strasse || String(cd.strasse || cd.adresse || '').trim(),
      plz: prev.plz || String(cd.plz || '').trim(),
      ort: prev.ort || String(cd.ort || cd.stadt || '').trim(),
    }));
  }, [clientData]);

  const upcoming = useMemo(() => {
    return seminars
      .filter((s) => !isSeminarDateExpired(resolveSeminarDateText(s)))
      .sort((a, b) => {
        const da = parseSeminarEndDate(resolveSeminarDateText(a))?.getTime() ?? Number.MAX_SAFE_INTEGER;
        const db_ = parseSeminarEndDate(resolveSeminarDateText(b))?.getTime() ?? Number.MAX_SAFE_INTEGER;
        return da - db_;
      });
  }, [seminars]);

  const selected = upcoming.find((s) => s.id === selectedId) || null;

  const updateField = (field: keyof RegistrationFormData, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (submitError) setSubmitError(null);
  };

  const openDetail = (id: string) => {
    setSelectedId(id);
    setSubmitSuccess(false);
    setSubmitError(null);
    setInvalidFields([]);
    setIsPitchExpanded(false);
    setZusatzPersonen(0);
    setPromoCodeInput('');
    setPromoCodeError(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const matchedPromo = useMemo(
    () => (selected ? findMatchingPromoCode(promoCodeInput, selected.promoCodes) : null),
    [selected, promoCodeInput]
  );

  const handleApplyPromoButton = () => {
    if (!promoCodeInput.trim()) {
      setPromoCodeError(null);
      return;
    }
    if (!matchedPromo) {
      setPromoCodeError('Dieser Code ist für dieses Seminar nicht gültig.');
    } else {
      setPromoCodeError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected) return;

    const trimmed = {
      vorname: form.vorname.trim(),
      nachname: form.nachname.trim(),
      firma: form.firma.trim(),
      email: form.email.trim(),
      telefon: form.telefon.trim(),
      strasse: form.strasse.trim(),
      plz: form.plz.trim(),
      ort: form.ort.trim(),
    };

    setSubmitError(null);
    setInvalidFields([]);

    const currentInvalid: string[] = [];
    const errors: string[] = [];

    if (!trimmed.vorname) currentInvalid.push('vorname');
    if (!trimmed.nachname) currentInvalid.push('nachname');
    if (!trimmed.firma) currentInvalid.push('firma');
    if (!trimmed.strasse) currentInvalid.push('strasse');
    if (!trimmed.plz) currentInvalid.push('plz');
    if (!trimmed.ort) currentInvalid.push('ort');
    if (currentInvalid.length) errors.push('Bitte fülle alle Pflichtfelder aus.');

    if (!mandantenNummer.trim()) {
      currentInvalid.push('mandantenNummer');
      errors.push('Bitte trage deine Mandant-Nummer ein.');
    }

    if (!EMAIL_REGEX.test(trimmed.email)) {
      currentInvalid.push('email');
      errors.push('Bitte gib eine gültige E-Mail-Adresse ein.');
    }

    if (!trimmed.telefon || !PHONE_REGEX.test(trimmed.telefon)) {
      currentInvalid.push('telefon');
      errors.push('Bitte prüfe deine Telefonnummer.');
    }

    if (!form.agbAccepted) {
      currentInvalid.push('dsgvo');
      errors.push('Du musst den Datenschutzbestimmungen zustimmen.');
    }

    setInvalidFields(currentInvalid);
    if (currentInvalid.length > 0) {
      setSubmitError(errors[0] || null);
      return;
    }

    const pricing = calculateSeminarPricingWithPromo({
      basisPreis: selected.priceBestand,
      zusatzPreisTotal: zusatzPersonen * selected.priceZusatz,
      promo: matchedPromo,
    });
    const {
      basisPreis,
      zusatzPreisTotal,
      discountNetto,
      discountLabel,
      nettoGesamt,
      mwst,
      bruttoGesamt,
    } = pricing;

    setIsSubmitting(true);
    try {
      await addDoc(collection(db, 'seminar_anmeldungen'), {
        eventId: selected.id,
        status: 'neu',
        createdAt: serverTimestamp(),
        source: 'peone_seminare_page',
        formData: {
          isMandant: true,
          mandantenNummer: mandantenNummer.trim(),
          vorname: trimmed.vorname,
          nachname: trimmed.nachname,
          firma: trimmed.firma,
          zusatzPersonen,
          promoCode: matchedPromo?.code || '',
          email: trimmed.email,
          telefon: trimmed.telefon,
          strasse: trimmed.strasse,
          plz: trimmed.plz,
          ort: trimmed.ort,
        },
        financials: {
          basisPreis,
          zusatzPreisTotal,
          promoCode: matchedPromo?.code || '',
          discountNetto,
          discountLabel,
          nettoGesamt,
          mwst,
          bruttoGesamt,
        },
        legal: {
          dsgvoAkzeptiert: form.agbAccepted,
          fotoVideoAkzeptiert: form.photoConsent,
        },
      });
      setSubmitSuccess(true);
    } catch (err) {
      console.error('[SeminarePage] Anmeldung fehlgeschlagen', err);
      setSubmitError(
        'Die Anmeldung konnte nicht gespeichert werden. Bitte versuche es erneut oder kontaktiere uns direkt.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center text-pe-accent-text dark:text-[#E6C299] font-black uppercase tracking-widest text-sm">
        <Loader2 className="w-5 h-5 animate-spin mr-3" /> Seminare werden geladen…
      </div>
    );
  }

  // ——— DETAIL / LANDING OPTIK + ANMELDUNG ———
  if (selected) {
    const pricing = calculateSeminarPricingWithPromo({
      basisPreis: selected.priceBestand,
      zusatzPreisTotal: zusatzPersonen * selected.priceZusatz,
      promo: matchedPromo,
    });
    const {
      basisPreis,
      zusatzPreisTotal,
      discountNetto,
      discountLabel,
      nettoGesamt,
      mwst,
      bruttoGesamt,
    } = pricing;

    return (
      <div className="min-h-screen bg-pe-bg text-pe-muted font-sans -mx-2 md:mx-0 rounded-xl overflow-hidden">
        <div className="px-4 md:px-8 pt-6">
          <button
            type="button"
            onClick={() => setSelectedId(null)}
            className="mb-4 flex items-center gap-2 text-pe-muted dark:text-slate-400 hover:text-[#8D6435] dark:hover:text-[#E6C299] text-xs font-bold uppercase tracking-widest transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Alle Seminare
          </button>
        </div>

        <header className="w-full">
          {selected.marketingImage && (
            <div className="w-full bg-black">
              <img
                src={selected.marketingImage}
                alt={selected.title}
                className="w-full h-auto max-h-none object-contain object-center block"
              />
            </div>
          )}
          <div className="px-4 md:px-8 pt-8 pb-8 max-w-5xl mx-auto">
            <div className="text-left">
              <span className="inline-block text-[10px] font-black uppercase tracking-[0.3em] text-pe-accent-text dark:text-[#E6C299] border border-pe-border dark:border-[#333] bg-stone-100 dark:bg-[#0a0a0a] px-3 py-1.5 rounded-full mb-6">
                PreisExpert Seminar
              </span>
              <h1 className="text-4xl lg:text-5xl font-black text-pe-text dark:text-white leading-tight tracking-tight mb-4">
                {selected.title}
              </h1>
              <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 mb-6">
                {(() => {
                  const days = sortScheduleDays(normalizeScheduleDays(selected.scheduleDays));
                  if (days.length > 0) {
                    return days.map((day) => (
                      <span
                        key={day.id}
                        className="inline-flex items-center gap-2 text-sm text-pe-muted border border-pe-border dark:border-[#333] bg-stone-100 dark:bg-[#0a0a0a] px-3 py-1.5 rounded-lg"
                      >
                        <Calendar className="w-4 h-4 text-pe-accent-text dark:text-[#E6C299] shrink-0" />
                        {formatScheduleDayLine(day)}
                      </span>
                    ));
                  }
                  if (selected.date) {
                    return (
                      <span className="inline-flex items-center gap-2 text-sm text-pe-muted border border-pe-border dark:border-[#333] bg-stone-100 dark:bg-[#0a0a0a] px-3 py-1.5 rounded-lg">
                        <Calendar className="w-4 h-4 text-pe-accent-text dark:text-[#E6C299]" />
                        {selected.date}
                      </span>
                    );
                  }
                  return null;
                })()}
                {(selected.location || selected.locationAddress) && (
                  <span className="inline-flex items-center gap-2 text-sm text-pe-muted border border-pe-border dark:border-[#333] bg-stone-100 dark:bg-[#0a0a0a] px-3 py-1.5 rounded-lg">
                    <MapPin className="w-4 h-4 text-pe-accent-text dark:text-[#E6C299]" />
                    {selected.locationAddress || selected.location}
                  </span>
                )}
              </div>
              {selected.marketingTeaser && (
                <p className="text-pe-muted text-lg leading-relaxed whitespace-pre-line">
                  {selected.marketingTeaser}
                </p>
              )}
            </div>
          </div>
        </header>

        {(selected.marketingBullets?.length || selected.marketingPitch) ? (
          <div className="max-w-7xl mx-auto px-4 md:px-8 pb-8">
            {!!selected.marketingBullets?.length && (
              <section className="mb-10">
                <div className="flex items-center gap-4 mb-6">
                  <h2 className="text-[11px] font-black uppercase tracking-[0.28em] text-pe-accent-text dark:text-[#E6C299] shrink-0">
                    Agenda & Highlights
                  </h2>
                  <div className="h-px flex-1 bg-gradient-to-r from-[#E6C299]/40 to-transparent" />
                </div>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                  {selected.marketingBullets!.map((bullet, index) => (
                    <li
                      key={index}
                      className="flex gap-3.5 items-start rounded-2xl border border-pe-border dark:border-[#333] bg-white/80 dark:bg-[#0a0a0a] px-5 py-4"
                    >
                      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#8D6435]/35 dark:border-[#E6C299]/35 bg-[#E6C299]/10">
                        <Check className="w-4 h-4 text-pe-accent-text dark:text-[#E6C299]" strokeWidth={2.5} />
                      </span>
                      <span className="text-pe-muted dark:text-slate-300 text-[15px] leading-snug whitespace-pre-line pt-1">
                        {bullet}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            )}
            {selected.marketingPitch && (
              <section className="mb-2 rounded-2xl border border-pe-border dark:border-[#333] bg-white/60 dark:bg-[#0a0a0a] overflow-hidden">
                <div className="flex items-center gap-4 px-6 pt-6 pb-2">
                  <h2 className="text-[11px] font-black uppercase tracking-[0.28em] text-pe-accent-text dark:text-[#E6C299] shrink-0">
                    Über dieses Seminar
                  </h2>
                  <div className="h-px flex-1 bg-gradient-to-r from-[#E6C299]/40 to-transparent" />
                </div>
                <div className="px-6 pb-6">
                  <div
                    className={`relative pl-5 border-l-2 border-[#8D6435]/40 dark:border-[#E6C299]/40 text-pe-muted dark:text-slate-300 text-[15px] leading-relaxed whitespace-pre-line ${
                      !isPitchExpanded ? 'line-clamp-4' : ''
                    }`}
                  >
                    {selected.marketingPitch}
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsPitchExpanded((p) => !p)}
                    className="mt-5 flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.2em] text-pe-accent-text dark:text-[#E6C299] hover:text-pe-text dark:hover:text-white"
                  >
                    MEHR ERFAHREN
                    <ChevronDown className={`w-4 h-4 transition-transform ${isPitchExpanded ? 'rotate-180' : ''}`} />
                  </button>
                </div>
              </section>
            )}
          </div>
        ) : null}

        <div className="max-w-4xl mx-auto px-4 md:px-8 py-12">
          <section className="mb-8 border border-pe-border dark:border-[#333] rounded-2xl p-6 bg-transparent">
            <h2 className="text-sm font-black uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299] mb-4">Dein Mandanten-Preis</h2>
            <ul className="space-y-2 text-sm text-pe-muted dark:text-slate-300">
              <li>
                Seminar (Bestand):{' '}
                <span className="text-pe-text dark:text-white font-semibold">{formatEuro(selected.priceBestand)}</span> (netto)
              </li>
              <li>
                Zusatz-Teilnehmer:{' '}
                <span className="text-pe-text dark:text-white font-semibold">{formatEuro(selected.priceZusatz)}</span> (netto)
              </li>
            </ul>
          </section>

          <section className="bg-pe-card dark:bg-[#0f172a] border border-[#8D6435]/20 dark:border-[#E6C299]/20 rounded-2xl p-6 md:p-8 shadow-[0_0_40px_rgba(230,194,153,0.05)]">
            <h2 className="text-xl font-bold text-pe-text dark:text-white mb-2">Jetzt anmelden</h2>
            <p className="text-stone-500 dark:text-slate-500 text-sm mb-6">
              Als eingeloggter Mandant gilt automatisch der Bestandspreis.
            </p>

            {submitSuccess ? (
              <div className="text-center py-8 px-4 bg-emerald-950/30 border border-emerald-800/50 rounded-xl">
                <Check className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
                <h3 className="text-lg font-bold text-pe-text dark:text-white mb-2">Anmeldung erfolgreich!</h3>
                <p className="text-pe-muted dark:text-slate-400 text-sm leading-relaxed">
                  Vielen Dank für deine Anmeldung zu „{selected.title}“. Wir melden uns zeitnah bei dir.
                </p>
                <button
                  type="button"
                  onClick={() => setSelectedId(null)}
                  className="mt-6 text-xs font-black uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299] hover:text-pe-text dark:hover:text-white"
                >
                  Zurück zur Übersicht
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-pe-muted dark:text-slate-400 text-xs font-semibold uppercase tracking-wider mb-2">
                    Mandant-Nummer *
                  </label>
                  <input
                    type="text"
                    value={mandantenNummer}
                    onChange={(e) => {
                      setMandantenNummer(e.target.value);
                      if (submitError) setSubmitError(null);
                    }}
                    className={
                      invalidFields.includes('mandantenNummer')
                        ? `${inputClasses} border-red-500 bg-red-950/20 ring-1 ring-red-500`
                        : inputClasses
                    }
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-pe-muted dark:text-slate-400 text-xs font-semibold uppercase tracking-wider mb-2">
                      Vorname *
                    </label>
                    <input
                      type="text"
                      value={form.vorname}
                      onChange={(e) => updateField('vorname', e.target.value)}
                      className={
                        invalidFields.includes('vorname')
                          ? `${inputClasses} border-red-500 bg-red-950/20`
                          : inputClasses
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-pe-muted dark:text-slate-400 text-xs font-semibold uppercase tracking-wider mb-2">
                      Nachname *
                    </label>
                    <input
                      type="text"
                      value={form.nachname}
                      onChange={(e) => updateField('nachname', e.target.value)}
                      className={
                        invalidFields.includes('nachname')
                          ? `${inputClasses} border-red-500 bg-red-950/20`
                          : inputClasses
                      }
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-pe-muted dark:text-slate-400 text-xs font-semibold uppercase tracking-wider mb-2">
                    Salon / Firma *
                  </label>
                  <input
                    type="text"
                    value={form.firma}
                    onChange={(e) => updateField('firma', e.target.value)}
                    className={
                      invalidFields.includes('firma')
                        ? `${inputClasses} border-red-500 bg-red-950/20`
                        : inputClasses
                    }
                  />
                </div>

                <div>
                  <label className="block text-pe-muted dark:text-slate-400 text-xs font-semibold uppercase tracking-wider mb-3">
                    Wie viele Mitarbeiter bringst du zusätzlich mit?
                  </label>
                  <div className="flex items-center justify-center gap-6">
                    <button
                      type="button"
                      onClick={() => setZusatzPersonen((p) => Math.max(0, p - 1))}
                      className="w-14 h-14 flex items-center justify-center bg-stone-200 dark:bg-slate-800 hover:bg-stone-300 dark:hover:bg-slate-700 text-pe-accent-text dark:text-[#E6C299] text-2xl font-bold rounded-xl border border-pe-border-strong dark:border-slate-700"
                    >
                      −
                    </button>
                    <span className="text-4xl font-black text-pe-text dark:text-white w-16 text-center tabular-nums">
                      {zusatzPersonen}
                    </span>
                    <button
                      type="button"
                      onClick={() => setZusatzPersonen((p) => p + 1)}
                      className="w-14 h-14 flex items-center justify-center bg-stone-200 dark:bg-slate-800 hover:bg-stone-300 dark:hover:bg-slate-700 text-pe-accent-text dark:text-[#E6C299] text-2xl font-bold rounded-xl border border-pe-border-strong dark:border-slate-700"
                    >
                      +
                    </button>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-pe-muted dark:text-slate-400 text-xs font-semibold uppercase tracking-wider mb-2">
                      E-Mail *
                    </label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => updateField('email', e.target.value)}
                      className={
                        invalidFields.includes('email')
                          ? `${inputClasses} border-red-500 bg-red-950/20`
                          : inputClasses
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-pe-muted dark:text-slate-400 text-xs font-semibold uppercase tracking-wider mb-2">
                      Telefon (WhatsApp) *
                    </label>
                    <input
                      type="tel"
                      value={form.telefon}
                      onChange={(e) => updateField('telefon', e.target.value)}
                      className={
                        invalidFields.includes('telefon')
                          ? `${inputClasses} border-red-500 bg-red-950/20`
                          : inputClasses
                      }
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-pe-muted dark:text-slate-400 text-xs font-semibold uppercase tracking-wider mb-2">
                    Straße *
                  </label>
                  <input
                    type="text"
                    value={form.strasse}
                    onChange={(e) => updateField('strasse', e.target.value)}
                    className={
                      invalidFields.includes('strasse')
                        ? `${inputClasses} border-red-500 bg-red-950/20`
                        : inputClasses
                    }
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-pe-muted dark:text-slate-400 text-xs font-semibold uppercase tracking-wider mb-2">
                      PLZ *
                    </label>
                    <input
                      type="text"
                      value={form.plz}
                      onChange={(e) => updateField('plz', e.target.value)}
                      className={
                        invalidFields.includes('plz')
                          ? `${inputClasses} border-red-500 bg-red-950/20`
                          : inputClasses
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-pe-muted dark:text-slate-400 text-xs font-semibold uppercase tracking-wider mb-2">
                      Ort *
                    </label>
                    <input
                      type="text"
                      value={form.ort}
                      onChange={(e) => updateField('ort', e.target.value)}
                      className={
                        invalidFields.includes('ort')
                          ? `${inputClasses} border-red-500 bg-red-950/20`
                          : inputClasses
                      }
                    />
                  </div>
                </div>

                <div className="pt-4 space-y-3 border-t border-pe-border dark:border-slate-800">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.agbAccepted}
                      onChange={(e) => updateField('agbAccepted', e.target.checked)}
                      className="mt-1 accent-[#E6C299] w-4 h-4 shrink-0"
                    />
                    <span className="text-sm text-pe-muted dark:text-slate-400">
                      Ich habe die{' '}
                      <button
                        type="button"
                        onClick={() => setIsDsgvoModalOpen(true)}
                        className="text-pe-accent-text dark:text-[#E6C299] hover:underline"
                      >
                        Datenschutzbestimmungen
                      </button>{' '}
                      gelesen und akzeptiere diese. *
                    </span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.photoConsent}
                      onChange={(e) => updateField('photoConsent', e.target.checked)}
                      className="mt-1 accent-[#E6C299] w-4 h-4 shrink-0"
                    />
                    <span className="text-sm text-pe-muted dark:text-slate-400">
                      Foto-/Videoaufnahmen für Marketingzwecke (optional)
                    </span>
                  </label>
                </div>

                {/* EINLADUNGS- / RABATTCODE */}
                <div className="pt-4 border-t border-pe-border dark:border-slate-800">
                  <label className="block text-pe-muted dark:text-slate-400 text-xs font-semibold uppercase tracking-wider mb-2">
                    Einladungs- oder Rabattcode (optional)
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={promoCodeInput}
                      onChange={(e) => {
                        setPromoCodeInput(e.target.value.toUpperCase());
                        if (promoCodeError) setPromoCodeError(null);
                      }}
                      placeholder="Code eingeben (z. B. VIP100)"
                      className={`${inputClasses} uppercase font-mono`}
                    />
                    <button
                      type="button"
                      onClick={handleApplyPromoButton}
                      className="shrink-0 px-5 rounded-xl border border-[#E6C299]/50 bg-[#E6C299]/15 hover:bg-[#E6C299] text-pe-accent-text dark:text-[#E6C299] hover:text-black text-xs font-black uppercase tracking-widest transition-colors"
                    >
                      Einlösen
                    </button>
                  </div>
                  {matchedPromo && (
                    <p className="mt-2 text-xs font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5">
                      <Check className="w-4 h-4 shrink-0" />
                      Code eingelöst: {discountLabel}
                    </p>
                  )}
                  {!matchedPromo && promoCodeError && (
                    <p className="mt-2 text-xs font-semibold text-red-500 dark:text-red-400">
                      {promoCodeError}
                    </p>
                  )}
                </div>

                <div className="mt-4 p-5 border border-pe-border dark:border-slate-800 rounded-xl bg-stone-100/60 dark:bg-black/30 space-y-2 text-sm">
                  <div className="flex justify-between text-pe-muted dark:text-slate-400">
                    <span>Seminar-Preis (Mandant, netto)</span>
                    <span className="text-pe-text dark:text-white font-mono">{formatEuro(basisPreis)}</span>
                  </div>
                  {zusatzPersonen > 0 && (
                    <div className="flex justify-between text-pe-muted dark:text-slate-400">
                      <span>
                        Zusatz ({zusatzPersonen} × {formatEuro(selected.priceZusatz)})
                      </span>
                      <span className="text-pe-text dark:text-white font-mono">{formatEuro(zusatzPreisTotal)}</span>
                    </div>
                  )}
                  {discountNetto > 0 && (
                    <div className="flex justify-between text-emerald-700 dark:text-emerald-400 font-semibold">
                      <span>Rabatt ({matchedPromo?.code})</span>
                      <span className="font-mono">−{formatEuro(discountNetto)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-pe-muted dark:text-slate-400 border-t border-pe-border dark:border-slate-800 pt-2">
                    <span>Netto gesamt</span>
                    <span className="text-pe-text dark:text-white font-mono">{formatEuro(nettoGesamt)}</span>
                  </div>
                  <div className="flex justify-between text-pe-muted dark:text-slate-400">
                    <span>MwSt. (19%)</span>
                    <span className="text-pe-text dark:text-white font-mono">{formatEuro(mwst)}</span>
                  </div>
                  <div className="flex justify-between text-pe-accent-text dark:text-[#E6C299] font-bold border-t border-pe-border-strong dark:border-slate-700 pt-2 text-base">
                    <span>Gesamt (brutto)</span>
                    <span className="font-mono">{formatEuro(bruttoGesamt)}</span>
                  </div>
                </div>

                {submitError && (
                  <div className="flex items-start gap-3 bg-red-950/30 border border-red-800/50 text-red-300 text-sm rounded-xl p-4">
                    <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                    <span>{submitError}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full mt-4 bg-[#E6C299] hover:bg-[#d9b183] dark:hover:bg-white text-black font-black uppercase tracking-widest py-4 rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" /> Wird gesendet…
                    </>
                  ) : (
                    'Verbindlich anmelden'
                  )}
                </button>
              </form>
            )}
          </section>
        </div>

        <DsgvoModal isOpen={isDsgvoModalOpen} onClose={() => setIsDsgvoModalOpen(false)} />
      </div>
    );
  }

  // ——— ÜBERSICHT ———
  return (
    <div className="min-h-screen bg-pe-bg text-pe-muted font-sans -mx-2 md:mx-0 rounded-xl overflow-hidden pb-16">
      <header className="px-6 md:px-10 pt-10 pb-8 border-b border-pe-border dark:border-[#333]">
        <span className="inline-block text-[10px] font-black uppercase tracking-[0.3em] text-pe-accent-text dark:text-[#E6C299] mb-3">
          Verwaltung & Mandat
        </span>
        <h1 className="text-3xl md:text-4xl font-black text-pe-text dark:text-white tracking-tight mb-2">
          Geplante Seminare
        </h1>
        <p className="text-pe-muted text-sm md:text-base max-w-2xl">
          Alle kommenden PreisExpert-Seminare — mit Landingpage-Infos und direkter Anmeldung zum
          Mandantenpreis.
        </p>
      </header>

      <div className="w-full px-4 md:px-8 py-10">
        {upcoming.length === 0 ? (
          <div className="text-center py-20 border border-pe-border dark:border-[#333] rounded-2xl bg-white/50 dark:bg-[#0a0a0a]">
            <Calendar className="w-12 h-12 text-stone-500 mx-auto mb-4" />
            <p className="text-pe-muted text-sm">Aktuell sind keine geplanten Seminare freigeschaltet.</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-8">
            {upcoming.map((s) => (
              <article
                key={s.id}
                className={`group flex flex-col max-w-full mx-auto rounded-2xl border border-pe-border dark:border-[#333] bg-white/80 dark:bg-[#0a0a0a] overflow-hidden hover:border-[#8D6435]/40 dark:hover:border-[#E6C299]/40 transition-colors shadow-lg ${
                  s.marketingImage ? 'w-fit' : 'w-full max-w-xl'
                }`}
              >
                {s.marketingImage ? (
                  <div className="bg-black overflow-hidden relative">
                    <img
                      src={s.marketingImage}
                      alt=""
                      className="block h-auto w-auto max-w-2xl"
                    />
                    {s.isOnline && (
                      <span className="absolute top-3 left-3 text-[9px] font-black uppercase tracking-widest bg-blue-500/20 text-blue-300 border border-blue-500/40 px-2 py-1 rounded">
                        Online
                      </span>
                    )}
                  </div>
                ) : null}
                <div className="flex flex-col flex-1 p-5 md:p-6">
                  <h2 className="text-xl font-black text-pe-text dark:text-white mb-3 leading-tight">{s.title}</h2>
                  <div className="flex flex-col gap-1.5 mb-4">
                    {(() => {
                      const days = sortScheduleDays(normalizeScheduleDays(s.scheduleDays));
                      if (days.length > 0) {
                        return days.map((day) => (
                          <span
                            key={day.id}
                            className="inline-flex items-center gap-1.5 text-xs text-pe-muted border border-pe-border-strong dark:border-[#333] bg-stone-100 dark:bg-[#050505] px-2.5 py-1 rounded-lg w-fit"
                          >
                            <Calendar className="w-3.5 h-3.5 text-pe-accent-text dark:text-[#E6C299] shrink-0" />
                            {formatScheduleDayLine(day)}
                          </span>
                        ));
                      }
                      if (s.date) {
                        return (
                          <span className="inline-flex items-center gap-1.5 text-xs text-pe-muted border border-pe-border-strong dark:border-[#333] bg-stone-100 dark:bg-[#050505] px-2.5 py-1 rounded-lg w-fit">
                            <Calendar className="w-3.5 h-3.5 text-pe-accent-text dark:text-[#E6C299]" />
                            {s.date}
                          </span>
                        );
                      }
                      return null;
                    })()}
                    {(s.location || s.locationAddress) && (
                      <span className="inline-flex items-center gap-1.5 text-xs text-pe-muted border border-pe-border-strong dark:border-[#333] bg-stone-100 dark:bg-[#050505] px-2.5 py-1 rounded-lg w-fit">
                        <MapPin className="w-3.5 h-3.5 text-pe-accent-text dark:text-[#E6C299]" />
                        {s.location || s.locationAddress}
                      </span>
                    )}
                  </div>
                  {s.marketingTeaser && (
                    <p className="text-sm text-pe-muted leading-relaxed mb-4 flex-1 whitespace-pre-line">
                      {s.marketingTeaser}
                    </p>
                  )}
                  <div className="flex items-end justify-between gap-3 mt-auto pt-2 border-t border-pe-border dark:border-[#333]">
                    <div>
                      <p className="text-[9px] uppercase tracking-widest text-stone-500 font-bold">
                        Mandantenpreis
                      </p>
                      <p className="text-pe-accent-text dark:text-[#E6C299] font-black text-lg">
                        {formatEuro(s.priceBestand)}
                        <span className="text-xs font-normal text-stone-500 ml-1">netto</span>
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => openDetail(s.id)}
                      className="px-4 py-2.5 rounded-xl bg-[#E6C299] hover:bg-[#d9b183] dark:hover:bg-white text-black text-[10px] font-black uppercase tracking-widest transition-colors"
                    >
                      Details & Anmelden
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SeminarePage;
